import { useState } from "react";
import styled from "styled-components";
import { FiInfo } from "react-icons/fi";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { Paragraph } from "../../components/Paragraph";
import { StatusBadge, StatusIntent } from "../../components/StatusBadge";
import { TableBody } from "../../components/Table/TableBody";
import { TableCell } from "../../components/Table/TableCell";
import { TableHead } from "../../components/Table/TableHead";
import { TableRow } from "../../components/Table/TableRow";
import { Tooltip } from "../../components/Tooltip";
import { ElevatedIdResult } from "../../types/Individual";
import { CheckTableCell, CheckTableHeaderRow } from "./CheckStyledComponents";
import { DataTable } from "./ScreeningResultPrimitives";

/**
 * Pieces of the provider's elevated ID and liveness result
 * (`instance.elevated_result`), placed alongside the standard result they
 * expand on: quality checks above the matching document image, NFC steps
 * under the document details, validation details under the Document
 * validation check. Each piece renders nothing
 * when its list is empty, so checks without an elevated result look as
 * they did before.
 */

export type QualityCheck = NonNullable<
  ElevatedIdResult["quality_check_details"]
>[number];
export type ValidationDetail = NonNullable<
  ElevatedIdResult["validation_details"]
>[number];

/**
 * verify-service passes the provider's numeric `result` and `state` codes
 * through untranslated. In sample responses 1 and 2 line up with Passed and
 * Failed (the code 2 entries match the journey's failed document validation
 * and face match). Any other code is shown as its number rather than guessed.
 */
const RESULT_CODES: Record<number, { label: string; intent: StatusIntent }> = {
  1: { label: "Passed", intent: "success" },
  2: { label: "Failed", intent: "danger" },
};

const describeCode = (code?: number | null) => {
  if (code === null || code === undefined) {
    return { label: "Not reported", intent: "neutral" as StatusIntent };
  }
  return RESULT_CODES[code] ?? { label: `Code ${code}`, intent: "neutral" };
};

const CodeBadge = ({ code }: { code?: number | null }) => {
  const { label, intent } = describeCode(code);
  return <StatusBadge intent={intent}>{label}</StatusBadge>;
};

export const isFailedCode = (code?: number | null) => code === 2;

/** Failed first, then anything unrecognised, then passed. */
const codeRank = (code?: number | null) =>
  code === 2 ? 0 : code === 1 ? 2 : 1;

/** "front", "back" or null, from a provider side such as "Front". */
export const normaliseSide = (side?: string | null) => {
  const value = (side ?? "").toLowerCase();
  if (value.includes("front")) return "front";
  if (value.includes("back")) return "back";
  return null;
};

/** "Image Quality Glare Check" >> "Glare"; "Full Document In View Check" >> "Full document in view". */
const shortQualityLabel = (check: QualityCheck) => {
  const label = (check.title || check.name || "-")
    .replace(/^Image Quality\s+/i, "")
    .replace(/\s+Check$/i, "");
  return label.charAt(0) + label.slice(1).toLowerCase();
};

/** A check name and its result badge on one ruled line. */
const CheckRow = styled.div`
  ${({ theme: { colors, fontFamily, fontSizes, space } }) => `
    font-family: ${fontFamily};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${space[2]};
    padding: ${space[1]} 0;
    border-bottom: 1px solid ${colors.divider};
    font-size: ${fontSizes[1]};
    color: ${colors.gray[80]};
    min-width: 0;
  `}
`;

/** The info icon that marks a description, shown on hover. */
const InfoTip = ({ content }: { content: React.ReactNode }) => (
  <Tooltip content={content}>
    <Box
      display="inline-flex"
      alignItems="center"
      color="gray.40"
      style={{ cursor: "help" }}
    >
      <FiInfo size={12} />
    </Box>
  </Tooltip>
);

/**
 * Image quality checks for one side, failed first, as ruled rows of name and
 * result. An info icon beside the name carries the provider's description.
 */
export const QualityCheckList = ({ checks }: { checks: QualityCheck[] }) => {
  if (!checks.length) return null;
  return (
    <Box>
      {[...checks]
        .sort((a, b) => codeRank(a.state) - codeRank(b.state))
        .map((check, i) => (
          <CheckRow key={check.id ?? i}>
            <Flex alignItems="center" gap="4" minWidth={0}>
              <span>{shortQualityLabel(check)}</span>
              {check.description && (
                <InfoTip
                  content={
                    <>
                      <b>{check.title || check.name}</b>
                      <div>{check.description}</div>
                    </>
                  }
                />
              )}
            </Flex>
            <CodeBadge code={check.state} />
          </CheckRow>
        ))}
    </Box>
  );
};

export type JourneyStep = NonNullable<
  ElevatedIdResult["journey_steps"]
>[number];

/** Step results meaning the step was never attempted, so there is nothing to show. */
const NOT_ATTEMPTED = ["", "NOTREQUESTED", "NOTPROVIDED"];

/**
 * The NFC chip read and NFC cross-check journey steps, only where the journey
 * asked for them. Every non-NFC journey reports both as NOTREQUESTED.
 */
export const nfcSteps = (steps?: JourneyStep[]) =>
  (steps ?? []).filter(
    (step) =>
      /^nfc/i.test(step.type ?? "") &&
      !NOT_ATTEMPTED.includes((step.high_level_result ?? "").toUpperCase())
  );

/** Plain-English names and descriptions for the NFC journey steps. */
const NFC_STEPS: Record<string, { label: string; description: string }> = {
  NFC: {
    label: "NFC chip read",
    description:
      "The applicant's phone read the data held on the document's contactless chip.",
  },
  "NFC CROSS-CHECK": {
    label: "NFC cross-check",
    description:
      "The data read from the chip was compared with the details read from the printed document.",
  },
};

const nfcStepInfo = (type?: string | null) =>
  NFC_STEPS[(type ?? "").trim().toUpperCase()] ?? {
    label: (type ?? "").trim() || "NFC",
    description: "",
  };

/** Readable names for the provider's check codes; anything else is shown in sentence case. */
const CHECK_NAMES: Record<string, string> = {
  DOCUMENTOVERALLVALIDATION: "Document overall validation",
  DOCUMENTFRONTSIDETYPECHECK: "Document front side type check",
  UNDERAGERULE: "Underage rule",
  DOCUMENTBLOCKINGPOLICY: "Document blocking policy",
  DOCUMENTEXPIRY: "Document expiry",
  DOCUMENTSUPPORT: "Document support",
  DOCUMENTVALIDATION: "Document validation",
  DOCUMENTTYPEMATCH: "Document type match",
  CHIPPHOTOVALIDATION: "Chip photo validation",
  DOCUMENTBACKSIDECHECK: "Document back side check",
};

/** The fields named in cross reference checks, such as "BIRTHDATE CROSS REFERENCE". */
const FIELD_NAMES: Record<string, string> = {
  BIRTHDATE: "Date of birth",
  NATIONALITYCODE: "Nationality",
  DOCUMENTNUMBER: "Document number",
  LASTNAME: "Last name",
  FIRSTNAME: "First name",
  MIDDLENAME: "Middle name",
  EXPIRYDATE: "Expiry date",
  ISSUEDATE: "Issue date",
  SEX: "Sex",
  GENDER: "Gender",
};

/** Where a cross-referenced value was read from. */
const SOURCE_NAMES: Record<string, string> = {
  FRONTSIDE: "front of document",
  BACKSIDE: "back of document",
  NFC: "chip",
};

const STATUS_LABELS: Record<string, string> = {
  NOTNEEDED: "Not needed",
  NOTREQUESTED: "Not requested",
  NOTPROVIDED: "Not provided",
};

/** The provider's upper-case words in sentence case: "PASSED" >> "Passed". */
const sentenceCase = (value?: string | null) => {
  const text = (value ?? "").replace(/_/g, " ").trim();
  return text
    ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
    : "-";
};

const statusLabel = (status?: string | null) =>
  STATUS_LABELS[(status ?? "").trim().toUpperCase()] ?? sentenceCase(status);

const sourceName = (source: string) =>
  SOURCE_NAMES[source.trim().toUpperCase()] ?? source.trim().toLowerCase();

/**
 * A journey step's check as its code and status. The provider writes each as
 * "CODE:STATUS", and a cross reference code has a colon of its own
 * ("BIRTHDATE CROSS REFERENCE: FRONTSIDE - NFC:PASSED"). verify-service
 * splits at the first colon, so the two halves are rejoined here and split
 * at the last. A bare entry with no colon is a result, not a check.
 */
const parseStepCheck = (entry: { check: string | null; status: string }) => {
  const raw = entry.check
    ? `${entry.check}:${entry.status ?? ""}`
    : entry.status ?? "";
  const at = raw.lastIndexOf(":");
  if (at < 0) return null;
  const code = raw.slice(0, at).trim();
  const status = raw.slice(at + 1).trim();
  return code ? { code, status } : null;
};

/**
 * "BIRTHDATE CROSS REFERENCE: FRONTSIDE - NFC" >> "Date of birth", with
 * "Front of document vs chip" beneath; "CHIPPHOTOVALIDATION" >> "Chip photo
 * validation". Unknown codes are shown in sentence case.
 */
const describeStepCheck = (code: string) => {
  const cross = /^(.+?)\s+CROSS\s*REFERENCE\s*:\s*(.+?)\s+-\s+(.+)$/i.exec(
    code
  );
  if (cross) {
    const field = cross[1].replace(/\s+/g, "").toUpperCase();
    const from = sourceName(cross[2]);
    return {
      name: FIELD_NAMES[field] ?? sentenceCase(cross[1]),
      detail: `${from.charAt(0).toUpperCase()}${from.slice(1)} vs ${sourceName(
        cross[3]
      )}`,
    };
  }
  return {
    name:
      CHECK_NAMES[code.replace(/\s+/g, "").toUpperCase()] ?? sentenceCase(code),
    detail: null,
  };
};

/** Failed first, then anything else, then passed. */
const stepRank = (status?: string | null) => {
  const value = (status ?? "").toUpperCase();
  return value === "FAILED" ? 0 : value === "PASSED" ? 2 : 1;
};

const stepIntent = (result?: string | null): StatusIntent => {
  switch ((result ?? "").toUpperCase()) {
    case "PASSED":
      return "success";
    case "FAILED":
      return "danger";
    case "REFER":
    case "REVIEW":
      return "warning";
    default:
      return "neutral";
  }
};

const StepBadge = ({ result }: { result?: string | null }) => (
  <StatusBadge intent={stepIntent(result)}>{statusLabel(result)}</StatusBadge>
);

/** Side by side where there is room, one above the other where there is not. */
const NfcCardGrid = styled.div`
  ${({ theme: { space } }) => `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: ${space[2]};
  `}
`;

const NfcCard = styled.div`
  ${({ theme: { colors, fontFamily, radii, space } }) => `
    font-family: ${fontFamily};
    background: ${colors.gray[5]};
    border: 1px solid ${colors.gray[100]};
    border-radius: ${radii[1]};
    padding: ${space[2]};
    min-width: 0;
  `}
`;

/**
 * One card per NFC step: its result, what the step does (on an info icon
 * beside the name, like the quality checks), any failure reason,
 * and the individual checks the provider ran within it.
 */
export const NfcCards = ({ steps }: { steps: JourneyStep[] }) => {
  if (!steps.length) return null;
  return (
    <NfcCardGrid>
      {steps.map((step, i) => {
        const { label, description } = nfcStepInfo(step.type);
        const reason =
          step.failure_reason && step.failure_reason.toLowerCase() !== "none"
            ? step.failure_reason
            : null;
        const checks = (step.validation_checks ?? [])
          .map(parseStepCheck)
          .filter(Boolean)
          .sort((a, b) => stepRank(a.status) - stepRank(b.status));
        return (
          <NfcCard key={`${step.type}-${i}`}>
            <Flex justifyContent="space-between" alignItems="center" gap="8">
              <Flex alignItems="center" gap="4" minWidth={0}>
                <Paragraph m="0px" fontWeight="600" color="gray.80">
                  {label}
                </Paragraph>
                {description && (
                  <InfoTip
                    content={
                      <>
                        <b>{label}</b>
                        <div>{description}</div>
                      </>
                    }
                  />
                )}
              </Flex>
              <StepBadge result={step.high_level_result} />
            </Flex>
            {reason && (
              <Paragraph m="0px" mt={1} fontSize={1} color="gray.80">
                <b>Reason:</b> {reason}
              </Paragraph>
            )}
            {checks.length > 0 && (
              <Box mt={2}>
                {checks.map((check, j) => {
                  const { name, detail } = describeStepCheck(check.code);
                  return (
                    <CheckRow key={j}>
                      <Box minWidth={0}>
                        <div>{name}</div>
                        {detail && (
                          <Paragraph m="0px" fontSize={0} color="gray.50">
                            {detail}
                          </Paragraph>
                        )}
                      </Box>
                      <StepBadge result={check.status} />
                    </CheckRow>
                  );
                })}
              </Box>
            )}
          </NfcCard>
        );
      })}
    </NfcCardGrid>
  );
};

export const validationSummary = (details: ValidationDetail[]) => {
  const failed = details.filter((d) => isFailedCode(d.result)).length;
  return failed > 0
    ? `${failed} failed of ${details.length} validations`
    : `${details.length} validations`;
};

export const ValidationDetails = ({
  details,
}: {
  details: ValidationDetail[];
}) => {
  const [showPassed, setShowPassed] = useState(false);

  if (!details.length) return null;

  const sorted = [...details].sort(
    (a, b) => codeRank(a.result) - codeRank(b.result)
  );
  const passedCount = details.filter((d) => d.result === 1).length;
  const visible = showPassed ? sorted : sorted.filter((d) => d.result !== 1);
  const showScore = details.some(
    (d) => typeof d.score === "number" && d.score !== 0
  );

  return (
    <Box>
      {visible.length > 0 ? (
        <DataTable>
          <TableHead></TableHead>
          <TableBody>
            <CheckTableHeaderRow>
              <CheckTableCell>Validator</CheckTableCell>
              <CheckTableCell>Side</CheckTableCell>
              {showScore && <CheckTableCell>Score</CheckTableCell>}
              <CheckTableCell>Result</CheckTableCell>
            </CheckTableHeaderRow>
            {visible.map((detail, i) => (
              <TableRow key={i}>
                <TableCell borderBottom="none">
                  <Paragraph m="0px" color="gray.80" fontWeight="500">
                    {detail.name || "-"}
                  </Paragraph>
                  {detail.description && (
                    <Paragraph m="0px" fontSize={0} color="gray.50">
                      {detail.description}
                    </Paragraph>
                  )}
                </TableCell>
                <TableCell color="gray.60" borderBottom="none">
                  {detail.document_side || "-"}
                </TableCell>
                {showScore && (
                  <TableCell color="gray.60" borderBottom="none">
                    {detail.score ?? "-"}
                  </TableCell>
                )}
                <TableCell borderBottom="none">
                  <CodeBadge code={detail.result} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
      ) : (
        <Paragraph m="0px" color="gray.60">
          All {passedCount} validations passed.
        </Paragraph>
      )}
      {passedCount > 0 && (
        <Button
          variant="blueText"
          size="small"
          mt={1}
          px={0}
          onClick={() => setShowPassed((prev) => !prev)}
        >
          {showPassed
            ? "Hide passed validations"
            : `Show ${passedCount} passed validation${
                passedCount === 1 ? "" : "s"
              }`}
        </Button>
      )}
    </Box>
  );
};
